import { useState } from "react";
import { api, getVisitorId } from "../lib/api";
import { toast } from "sonner";
import {
  Send,
  Check,
  Coffee,
  Film,
  TreePine,
  Users,
  Wrench,
  Home,
  Sparkles,
  Utensils,
  Camera,
  Handshake,
} from "lucide-react";
import useSeo from "../hooks/useSeo";

const THEMES = [
  { id: "coffee-tasting", label: "Coffee tasting", icon: Coffee },
  { id: "watch-party", label: "Watch party", icon: Film },
  { id: "nature-walk", label: "Nature walk", icon: TreePine },
  { id: "elder-getaway", label: "Senior getaway", icon: Users },
  { id: "lifeskill", label: "Lifeskill workshop", icon: Wrench },
  { id: "other", label: "Something else", icon: Sparkles },
];

const CITIES = ["Hyderabad", "Bangalore", "Pune"];

const BRINGING = [
  { id: "venue", label: "A venue / space", icon: Home },
  { id: "skill", label: "A skill / to host", icon: Sparkles },
  { id: "food", label: "Food / drink", icon: Utensils },
  { id: "gear", label: "Gear (projector, camera etc.)", icon: Camera },
];

export default function Copartner() {
  useSeo({
    title: "Co-partner an event — saamne",
    description:
      "Have a rooftop, a skill, a curated reel, a Sunday morning? Co-partner an in-person experience with saamne in Hyderabad, Bangalore or Pune. Bring what you have — we'll bring the crowd and the crew.",
    path: "/copartner",
  });

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    city: "Hyderabad",
    category: "watch-party",
    bringing: [],
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const toggleBring = (id) => {
    setForm((f) => ({
      ...f,
      bringing: f.bringing.includes(id)
        ? f.bringing.filter((x) => x !== id)
        : [...f.bringing, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone, please.");
      return;
    }
    setSending(true);
    try {
      await api.post("/copartners", {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        city: form.city,
        category: form.category,
        bringing: form.bringing,
        message: form.message.trim() || null,
        visitor_id: getVisitorId(),
      });
      setDone(true);
      toast.success("Beautiful. We'll reach out to co-plan.");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="copartner-page"
      className="max-w-3xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-24"
    >
      <div className="label-eyebrow">Host with us</div>
      <h1 className="font-serif text-5xl md:text-6xl mt-4 leading-tight">
        Partner with <em style={{ color: "var(--highlight)" }}>us</em>.
      </h1>
      <p className="mt-5 text-lg max-w-2xl" style={{ color: "var(--text-muted)" }}>
        A rooftop in Koramangala. A Sunday morning nature-walk you've done for years. A curated Ghibli reel. A grandmother who cooks a legendary bisi bele bath. Bring what you have — we'll bring the crowd, the invite list and a crew to help you host.
      </p>

      <section
        className="mt-10 space-y-4 text-base leading-relaxed"
        style={{ color: "var(--text-main)" }}
      >
        <h2 className="font-serif text-2xl md:text-3xl leading-tight">
          How co-partnering works
        </h2>
        <p>
          You tell us what you'd love to host and what you'd bring — a space, a
          skill, a Sunday, a story. We take care of the rest: invitations, a
          hand-picked group of guests, a small crew to help on the day, and any
          extra gear you might need. We split the joy (and any small costs)
          transparently. No shiny sponsorship decks, no marketing gymnastics —
          just a room full of people who really wanted to be there.
        </p>
        <p>
          Great co-partners so far have included home cooks with a spare
          Sunday, indie filmmakers who wanted to screen their short in a room
          with actual chairs, a retired professor of literature, and an
          apartment-block WhatsApp group that just wanted to know their
          neighbours.
        </p>
      </section>

      <div className="mt-12">
        {done ? (
          <div
            data-testid="copartner-success"
            className="card-warm p-10 text-center"
          >
            <div
              className="mx-auto mb-5 flex items-center justify-center rounded-full"
              style={{
                width: 64,
                height: 64,
                background: "var(--surface)",
                color: "var(--primary)",
              }}
            >
              <Check size={32} />
            </div>
            <h3 className="font-serif text-3xl">Beautiful. Note received.</h3>
            <p className="mt-3 text-sm" style={{ color: "var(--text-muted)" }}>
              We'll reach out on the contact you left with us within a week to
              co-plan the details.
            </p>
          </div>
        ) : (
          <form
            onSubmit={submit}
            data-testid="copartner-form"
            className="form-card space-y-5"
          >
            <div>
              <div className="label-eyebrow mb-2">
                What would you love to co-host?
              </div>
              <div
                className="grid grid-cols-2 sm:grid-cols-3 gap-2"
                data-testid="copartner-theme-picker"
              >
                {THEMES.map((t) => {
                  const active = form.category === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      data-testid={`cop-theme-${t.id}`}
                      data-active={active}
                      onClick={() => setForm({ ...form, category: t.id })}
                      className="tile-select"
                    >
                      <t.icon size={20} />
                      <div className="font-serif text-lg mt-2 leading-snug">
                        {t.label}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="label-eyebrow mb-2">In…</div>
              <div
                className="grid grid-cols-3 gap-2"
                data-testid="copartner-city-picker"
              >
                {CITIES.map((c) => {
                  const active = form.city === c;
                  return (
                    <button
                      key={c}
                      type="button"
                      data-testid={`cop-city-${c.toLowerCase()}`}
                      data-active={active}
                      onClick={() => setForm({ ...form, city: c })}
                      className="pill-select"
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="label-eyebrow mb-2">What can you bring?</div>
              <div
                className="grid grid-cols-2 gap-2"
                data-testid="copartner-bringing-picker"
              >
                {BRINGING.map((b) => {
                  const active = form.bringing.includes(b.id);
                  return (
                    <button
                      key={b.id}
                      type="button"
                      data-testid={`cop-bring-${b.id}`}
                      data-active={active}
                      onClick={() => toggleBring(b.id)}
                      className="tile-select"
                    >
                      <b.icon size={18} />
                      <div className="text-sm mt-2 leading-snug">{b.label}</div>
                    </button>
                  );
                })}
              </div>
              <p
                className="text-xs mt-2"
                style={{ color: "var(--text-muted)" }}
              >
                Tick anything that applies — you can add more in the note below.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <input
                placeholder="Your name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                data-testid="cop-form-name"
              />
              <input
                type="tel"
                placeholder="Phone number"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                required
                data-testid="cop-form-phone"
              />
            </div>
            <input
              type="email"
              placeholder="Email (optional)"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="cop-form-email"
            />
            <textarea
              rows={4}
              placeholder="A few lines — what would you love to co-host, and what would make it feel like you?"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="cop-form-message"
            />
            <button
              type="submit"
              disabled={sending}
              className="btn-primary w-full justify-center"
              data-testid="cop-form-submit"
            >
              <Handshake size={18} />
              {sending ? "Sending…" : "Offer to co-host"}
            </button>
            <p
              className="text-xs text-center"
              style={{ color: "var(--text-muted)" }}
            >
              We reach out on the contact you leave with us — everything shared
              one-to-one, never posted publicly.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
